import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { AuthContext, writeAuthProfileCache } from "../context/AuthContext";
import { GoogleAuthButton } from "./google";
import { PrimaryButton } from "../../design-system";
import { typography, borders, textColors, interactive } from "../../design-system/tokens";
import { FormField, Input } from "../../design-system/components/inputs";
import { AuthNetworkErrorState } from "../../design-system/components/layouts";
import { EyeIcon, EyeOffIcon } from "../../design-system/icons";
import AuthLayout from "./AuthLayout";
import { apiFetch } from "../utils/api";
import { ingestReferralFromSearch } from "../utils/referralCoupon";

const Login = () => {
  const { setAuth } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    ingestReferralFromSearch(location.search);
  }, [location.search]);



  const submitLogin = async () => {
    setError("");
    setEmailError("");
    setPasswordError("");
    setIsNetworkError(false);
    const trimmedEmail = (email || "").trim();
    const trimmedPassword = (password || "").trim();
    let hasValidationError = false;
    if (!trimmedEmail) {
      setEmailError("Email or username is required");
      hasValidationError = true;
    }
    if (!trimmedPassword) {
      setPasswordError("Password is required");
      hasValidationError = true;
    }
    if (hasValidationError) {
      return;
    }
    try {
      setIsSubmitting(true);
      const res = await apiFetch("/api/auth/login/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, password: trimmedPassword, accepted_legal: true }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch (parseErr) {
        // ignore
      }

      if (res.ok) {
        const mustCompleteOnboarding = !!(data.must_complete_onboarding ?? data.user?.must_complete_onboarding);
        // Keep the local profile cache in sync so we never "rehydrate" into the previous user.
        // Login response uses `user_id` while profile cache expects `id`.
        writeAuthProfileCache({
          id: data.user_id,
          username: data.username,
          must_complete_onboarding: mustCompleteOnboarding,
        });
        setAuth({ userId: data.user_id, username: data.username, mustCompleteOnboarding });
        // ProtectedRoute also enforces this, but navigating straight there avoids a redirect bounce.
        navigate(mustCompleteOnboarding ? "/edit/complete-account" : `/edit/${data.username}`);
      } else {
        setError(data.error || "Incorrect email/username or password");
      }
    } catch (err) {
      console.error(err);
      setIsNetworkError(true);
      setError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitLogin();
  };

  return (
    <AuthLayout 
      title="Welcome back to"
      subtitle="Sign in to continue building your professional website"
    >
      <h2 className={`${typography.h2} ${textColors.primary} mb-6 text-center`}>Sign In</h2>
      {isNetworkError ? (
        <div className="mb-4">
          <AuthNetworkErrorState
            title="Network error :("
            description="We couldn't sign you in right now. Please check your connection."
            className="py-6"
          />
        </div>
      ) : error ? (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-center">
          {error}
        </div>
      ) : null}
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField 
          label="Email or Username" 
          htmlFor="email"
          required
          error={emailError}
        >
          <Input
            id="email"
            type="text"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            placeholder="Enter your email or username"
            required
          />
        </FormField>
        
        <FormField 
          label="Password" 
          htmlFor="password"
          required
          error={passwordError}
        >
          <Input
            id="password"
            type={isPasswordVisible ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError("");
            }}
            placeholder="Enter your password"
            rightAdornment={
              <button
                type="button"
                onMouseDown={(e) => {
                  // Prevent input blur when toggling via mouse
                  e.preventDefault();
                }}
                onClick={() => setIsPasswordVisible((v) => !v)}
                aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                aria-pressed={isPasswordVisible}
                className={`h-9 w-9 inline-flex items-center justify-center rounded-md transition-colors ${textColors.muted} ${textColors.hover} focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-500`}
              >
                {isPasswordVisible ? <EyeOffIcon size="sm" /> : <EyeIcon size="sm" />}
              </button>
            }
            required
          />
          <div className="mt-2 text-right">
            <Link
              to="/edit/recovery"
              className={`${typography.bodySmall} ${interactive.link} ${interactive.linkHover} underline font-medium transition-colors`}
            >
              Forgot password?
            </Link>
          </div>
        </FormField>
        
        <PrimaryButton
          type="submit"
          variant="gradient"
          fullWidth={true}
          loading={isSubmitting}
        >
          Sign In
        </PrimaryButton>
      </form>
      
      {/* Social login options */}
      <div className="mt-6">
        <div className="flex items-center gap-4">
          <div className={`flex-1 border-t ${borders.default}`} />
          <span className={`${typography.muted} ${textColors.tertiary}`}>or</span>
          <div className={`flex-1 border-t ${borders.default}`} />
        </div>
        
        <div className="mt-6">
          <GoogleAuthButton mode="login" />
        </div>
      </div>

      <p className={`${typography.caption} ${textColors.secondary} mt-6 text-center`}>
        By continuing, you agree to our{" "}
        <Link to="/terms" target="_blank" rel="noopener noreferrer" className={`underline font-medium ${interactive.link} ${interactive.linkHover}`}>
          Terms of Use
        </Link>{" "}
        and acknowledge our{" "}
        <Link to="/privacy" target="_blank" rel="noopener noreferrer" className={`underline font-medium ${interactive.link} ${interactive.linkHover}`}>
          Privacy Policy
        </Link>
        .
      </p>
      
      <div className="mt-6 text-center">
        <p className={`${typography.body} ${textColors.secondary}`}>
          Don't have an account?{" "}
          <Link
            to="/edit/register"
            className={`underline font-medium transition-colors ${interactive.link} ${interactive.linkHover}`}
          >
            Create account
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default Login;
